<template>
    <Transition name="fade">
        <div v-if="!isDismissed && !showAddedState" v-bind="$attrs">
            <div class="overflow-hidden rounded-lg border bg-white shadow-md">
                <template v-if="isInactive || collaborationProfileShared">
                    <div v-if="isInactive" class="status-row bg-valence-grey-50 text-valence-grey-750">
                        <i class="bi bi-slash-circle" aria-hidden="true"></i>
                        Inactive
                    </div>
                    <div v-else-if="collaborationProfileShared" class="status-row bg-valence-primary-100 text-valence-primary-500" data-testid="collaboration-profile-chip">
                        <i class="bi bi-stars" aria-hidden="true"></i>
                        Collaboration Profile
                    </div>
                </template>
                <BaseAccordion
                    :accordion-ids="['people']"
                    :header-class="isNewConnection ? 'hover:bg-transparent' : ''"
                    :trigger-class="isNewConnection ? 'px-5 py-4.5' : ''"
                    @update:model-value="onAccordionToggle">
                    <template #trigger-people>
                        <div class="flex items-center gap-3 grow text-left">
                            <div
                                class="shadow-sm flex items-center justify-center text-center rounded-full"
                                :class="
                                    isNewConnection
                                        ? 'min-w-10 max-w-10 min-h-10 max-h-10 bg-[#ECE7DE] nadia-chat-semibold'
                                        : 'min-w-8 max-w-8 min-h-8 max-h-8 bg-valence-primary-500 text-white body-small'
                                "
                                aria-hidden="true"
                                translate="no">
                                {{ getFirstLastInitials(name) }}
                            </div>
                            <div class="flex min-w-0 flex-col" :class="{ 'gap-1': !isNewConnection }">
                                <p class="nadia-chat-semibold leading-tight wrap-break-word">{{ name }}</p>
                                <p v-if="jobTitle" class="body-small" :class="{ 'text-valence-grey-750': isNewConnection }">{{ jobTitle }}</p>
                                <p v-else-if="isNewConnection" class="body-small text-valence-grey-750">Role not known</p>
                            </div>
                        </div>
                    </template>
                    <template v-if="isNewConnection" #expandIcon>
                        <span class="base-btn-neutral base-btn-sm shrink-0 whitespace-nowrap">Add {{ firstName }}</span>
                    </template>
                    <template #content-people>
                        <div :class="{ 'border-t': !isNewConnection }">
                            <template v-if="isWrongPersonView">
                                <div :class="isNewConnection ? 'px-5 pb-5' : 'p-3'">
                                    <ChatConnectionDisambiguation
                                        :show-cancel="true"
                                        :allow-create="true"
                                        :exclude-connection-id="connectionUuid"
                                        :initial-search-term="isNewConnection ? name : ''"
                                        @select="handleWrongPersonSelect"
                                        @create="handleWrongPersonCreate"
                                        @cancel="isWrongPersonView = false" />
                                </div>
                            </template>
                            <div v-else-if="isNewConnection" class="px-5 pb-5">
                                <!-- Add someone new -->
                                <HorizontalDivider class="mt-0 mb-5" />
                                <label :for="nameInputId" class="flex flex-col gap-1">
                                    <span class="scaffold-details-small text-valence-grey-750">Name</span>
                                    <input :id="nameInputId" v-model="form.name" placeholder="Name" class="body w-full rounded-md border border-valence-grey-250 p-2.5" />
                                </label>
                                <label :for="roleInputId" class="mt-2.5 flex flex-col gap-1">
                                    <span class="scaffold-details-small text-valence-grey-750">Role</span>
                                    <input
                                        :id="roleInputId"
                                        v-model="form.jobTitle"
                                        placeholder="Add their role"
                                        class="body w-full rounded-md border border-valence-grey-250 p-2.5" />
                                </label>
                                <!-- One-off charcoal override; drop once BaseButton primary supports this color. -->
                                <BaseButton
                                    theme="primary"
                                    class="mt-5 flex w-full items-center justify-center gap-2 bg-valence-grey-850 disabled:cursor-not-allowed disabled:opacity-40"
                                    :disabled="!isFormValid"
                                    @click="addNewPerson">
                                    <i class="bi bi-plus-lg" aria-hidden="true"></i>
                                    Add {{ firstName }}
                                </BaseButton>
                                <HorizontalDivider class="my-3.75" />
                                <ChatMyPeopleAccordionFooter @wrong-person="handleWrongPerson" @decline="handleDecline" />
                            </div>
                            <div v-else class="p-3">
                                <p v-if="profilePulled" data-testid="profile-pulled-summary" class="body-small mb-2 text-valence-grey-750">
                                    <ProfilePulledSummary />
                                </p>
                                <p v-if="summary || !profilePulled" class="body-small mb-4">
                                    {{ summaryStr }}
                                </p>
                                <button
                                    type="button"
                                    class="base-btn-base base-btn-sm flex w-full items-center justify-center gap-2 bg-valence-primary-500 text-white transition-colors hover:bg-valence-primary-600"
                                    @click="handleViewPerson">
                                    <i class="bi bi-box-arrow-up-right" aria-hidden="true"></i>
                                    View Person
                                </button>
                                <HorizontalDivider class="my-3.75" />
                                <ChatMyPeopleAccordionFooter @wrong-person="handleWrongPerson" @decline="handleDecline" />
                            </div>
                        </div>
                    </template>
                </BaseAccordion>
                <!-- Outside the accordion so linking no longer costs a chevron click, and collapsed-only
                     because the expanded view offers View Person as its primary action instead. -->
                <div v-if="showLinkSlot" class="border-t p-3">
                    <BaseButton
                        v-if="!isSearchOpen"
                        theme="tertiary"
                        size="sm"
                        class="flex w-full items-center justify-center gap-2 rounded-md border-2 border-dashed py-1.75"
                        data-testid="link-collaboration-profile"
                        @click="handleOpenSearch">
                        <i class="bi bi-plus-lg" aria-hidden="true"></i>
                        Link collaboration profile
                    </BaseButton>
                    <div v-else class="flex flex-col gap-2" data-testid="link-profile-search">
                        <div class="flex items-center gap-2">
                            <PublicProfileSearchCombobox
                                class="grow"
                                auto-focus
                                :initial-search-term="name"
                                :disabled="isLinking"
                                :actions="inviteActions"
                                @select="onLinkSelect"
                                @action="handleCopyInvite"
                                @update:query="searchQuery = $event"
                                @search-error="searchError = $event">
                                <template #no-match>
                                    <div class="flex gap-2 p-4 pb-2" data-testid="link-profile-no-match">
                                        <i class="bi bi-person-x text-valence-grey-750" aria-hidden="true"></i>
                                        <div class="flex min-w-0 flex-col gap-0.5">
                                            <p class="body-small-semibold">No profile for “{{ searchedName }}” at {{ orgLabel }}.</p>
                                            <p class="body-small text-valence-grey-750">Works whether or not they've set one up yet.</p>
                                        </div>
                                    </div>
                                </template>
                                <template #action="{ action, hasNoMatch }">
                                    <span
                                        :class="['flex items-center', hasNoMatch ? 'base-btn-primary base-btn-sm py-2 gap-2' : 'text-valence-primary-500 px-1 gap-3']"
                                        data-testid="invite-label">
                                        <span :class="['flex items-center justify-center', hasNoMatch ? 'text-lg/none' : 'border rounded-full size-8']">
                                            <i class="bi" :class="hasCopiedInvite ? 'bi-check-lg' : 'bi-link-45deg'" aria-hidden="true"></i>
                                        </span>
                                        <span>
                                            {{ inviteLabel(hasNoMatch) ?? action.label }}
                                        </span>
                                    </span>
                                </template>
                            </PublicProfileSearchCombobox>
                            <button
                                type="button"
                                class="body-small shrink-0 text-valence-grey-750 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                                :disabled="isLinking"
                                @click="handleCancelSearch">
                                Cancel
                            </button>
                        </div>
                        <BaseNotification v-if="searchError" theme="error" text="Searching for profiles failed." @dismiss="searchError = null" />
                        <BaseNotification v-if="linkProfileError" theme="error" text="Linking collaboration profile failed." @dismiss="linkProfileError = null" />
                        <BaseNotification v-if="inviteCopyFailed" theme="error" text="Copying the invite link failed." @dismiss="inviteCopyFailed = false" />
                    </div>
                </div>
            </div>
        </div>
        <div v-else-if="showAddedState" class="flex items-center gap-2.5 text-left rounded-lgr border border-valence-grey-250 px-5 py-3.75">
            <div
                class="bg-[#ECE7DE] min-w-8 max-w-8 min-h-8 max-h-8 flex items-center justify-center text-center rounded-full nadia-chat-semibold"
                aria-hidden="true"
                translate="no">
                {{ getFirstLastInitials(name) }}
            </div>
            <div class="flex min-w-0 flex-col">
                <p class="nadia-chat-semibold leading-tight wrap-break-word">{{ name }}</p>
                <p v-if="jobTitle" class="body-small text-valence-grey-750">{{ jobTitle }}</p>
                <p v-else class="body-small text-valence-grey-750">Role not known</p>
            </div>
            <span class="scaffold-details-small ml-auto flex shrink-0 items-center gap-1.5 rounded-full bg-valence-grey-50 px-3 py-0.5 text-valence-grey-850 whitespace-nowrap">
                <i class="bi bi-check-lg text-[16px] leading-none" aria-hidden="true"></i>
                Added
            </span>
        </div>
    </Transition>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import type { GlobalProperties, OrganizationDetails } from "~vue/appTypes";
import { ChatAction, type PluckedAction } from "~vue/chatActions";
import { copyShareUrl, profileInviteMessage } from "~vue/CollaborationProfile/composables/useProfileShareActions";
import BaseAccordion from "~vue/components/BaseAccordion.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import type { ComboboxAction } from "~vue/components/BaseCombobox.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import { useLinkPublicProfile } from "~vue/composables/useLinkPublicProfile";
import { useTimedShow } from "~vue/composables/useTimedShow.js";
import PublicProfileSearchCombobox from "~vue/Connections/PublicProfileSearchCombobox.vue";
import type { ActionState, UpdateActionState } from "~vue/types/chat";
import type { Connection, ConnectionProp } from "~vue/types/connection";
import type { ExistingPublicProfile } from "~vue/types/profile";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { getFirstLastInitials, getFirstName } from "~vue/utils/stringUtils";
import { computed, type ComputedRef, type FunctionalComponent, h, inject, reactive, ref, useId } from "vue";

import ChatConnectionDisambiguation from "./ChatConnectionDisambiguation.vue";
import ChatMyPeopleAccordionFooter from "./ChatMyPeopleAccordionFooter.vue";

type ChatMyPeopleAccordionProps = {
    connectionChatMessageAction: PluckedAction<ChatAction.TAGGED_PERSON>;
    execute: (action: ChatAction, params: Record<string, unknown>, options?: Record<string, unknown>) => Promise<void>;
    csUuid?: string;
    connections?: ComputedRef<ConnectionProp[]>;
    shouldShowSubmitted?: boolean;
    publicProfileEnabled?: boolean;
    organizationDetails?: ComputedRef<OrganizationDetails | null>;
    collaborationProfileInviteUrl?: string;
    showToast?: (message: string) => void;
};
const {
    connectionChatMessageAction,
    execute,
    csUuid = "",
    connections = computed(() => []),
    shouldShowSubmitted = false,
    publicProfileEnabled = false,
    organizationDetails: organizationDetailsProp = computed(() => null),
    collaborationProfileInviteUrl = "",
    showToast,
} = defineProps<ChatMyPeopleAccordionProps>();

// Each of these injects has a prop fallback because `useConnectionPopovers` mounts this card with a
// bare `render()`, which has no app context and so resolves every inject to its default.
const coachingSessionUuid = inject<string>("coachingSessionUuid", csUuid);
const allConnections = inject<ComputedRef<ConnectionProp[]>>("connections", connections);
const isPublicProfileEnabled = inject<boolean>("publicProfileEnabled", publicProfileEnabled);
const organizationDetails = inject<ComputedRef<OrganizationDetails | null>>("organization_details", organizationDetailsProp);
const inviteUrl = inject<string>("collaborationProfileInviteUrl", collaborationProfileInviteUrl);
const showChatToast = inject<(message: string) => void>("showChatToast", showToast ?? (() => {}));

const updateActionState = inject<UpdateActionState>("updateActionState", () => {});
const { emitter } = inject<GlobalProperties>("globalProperties", {} as GlobalProperties);

const DEFAULT_ACTION_STATE: ActionState = { dismissed: false, minimized: false, submitted: false };

const revertActionState = (patch: Partial<ActionState>) => {
    updateActionState({
        message_id: connectionChatMessageAction.message.chat_message_id,
        lineIdx: connectionChatMessageAction.lineIdx,
        action_state: { ...DEFAULT_ACTION_STATE, ...connectionChatMessageAction.line.action_state, ...patch },
    });
};

const handleActionFailure = (error: unknown, action: string) => {
    logError(error, { context: { component: "ChatMyPeopleAccordion", action }, tags: { error_type: "tagged_person_action" } });
    emitter.emit("error_notification", "Something went wrong updating this person. Please try again.");
};

const nameInputId = useId();
const roleInputId = useId();

const connectionUuid = computed(() => connectionChatMessageAction.line.action_params.connection);
const profileHref = computed(() => `/people/${connectionUuid.value}/`);
const currentConnection = computed(() => allConnections.value.find((conn) => conn.id === connectionUuid.value));
const name = computed(() => currentConnection.value?.name ?? connectionChatMessageAction.line.action_params.name);
const jobTitle = computed(() => currentConnection.value?.job_title ?? connectionChatMessageAction.line.action_params.title);
const isInactive = computed(() => currentConnection.value?.is_inactive ?? false);
const summary = computed(() => currentConnection.value?.summary);
const summaryStr = computed(() => summary.value ?? "No summary of this person is available. Chat more about them and I will come up with a summary about them.");

const profilePulled = computed(() => connectionChatMessageAction.line.action_params.profile_pulled ?? false);
const collaborationProfileShared = computed(() => connectionChatMessageAction.line.action_params.collaboration_profile_shared ?? false);
const hasNotes = computed(() => connectionChatMessageAction.line.action_params.has_notes ?? false);
const hasDetails = computed(() => connectionChatMessageAction.line.action_params.has_details ?? false);
const contextDescription = computed(() => {
    if (hasNotes.value && hasDetails.value) {
        return "your notes and details about this person";
    }
    if (hasNotes.value) {
        return "your notes about this person";
    }
    if (hasDetails.value) {
        return "the details you've saved about this person";
    }
    return "basic information about this person";
});

const isSubmitted = computed(() => connectionChatMessageAction.line.action_state?.submitted);
const isNewConnection = computed(() => {
    const params = connectionChatMessageAction.line.action_params;
    return !isSubmitted.value && params.was_created && (params.name || params.title || params.relationship);
});

const handleWrongPersonSelect = async (conn: Connection) => {
    isWrongPersonView.value = false;
    try {
        await execute(
            ChatAction.TAGGED_PERSON,
            {
                message_id: connectionChatMessageAction.message.chat_message_id,
                lineIdx: connectionChatMessageAction.lineIdx,
                index: connectionChatMessageAction.line.index,
                subaction: "reassign",
                correct_connection_id: conn.id,
                // Hide the card straight away rather than waiting on the response: `streaming: true`
                // only resolves once the whole assistant stream has finished. The server dismisses
                // this same action, and the picked person is confirmed by the tag it attaches to the
                // injected correction message.
                action_state: { dismissed: true },
            },
            { streaming: true },
        );
        logUserInteraction(AmplitudeEventNames.CONNECTION_DISAMBIGUATED, { connection_id: conn.id }, coachingSessionUuid);
    } catch (error) {
        revertActionState({ dismissed: false });
        handleActionFailure(error, "reassign");
    } finally {
        router.reload({ only: ["connections"] });
    }
};

const handleWrongPersonCreate = async (personName: string) => {
    isWrongPersonView.value = false;
    try {
        await execute(
            ChatAction.TAGGED_PERSON,
            {
                message_id: connectionChatMessageAction.message.chat_message_id,
                lineIdx: connectionChatMessageAction.lineIdx,
                index: connectionChatMessageAction.line.index,
                // Hide this card straight away rather than waiting on the response: `streaming: true`
                // only resolves once the whole assistant stream has finished. The server dismisses
                // this same action, and the added person is confirmed by the tag it attaches to the
                // injected correction message.
                action_state: { dismissed: true },
                subaction: "reassign_new",
                action_params: { name: personName },
            },
            { streaming: true },
        );
        logUserInteraction(AmplitudeEventNames.CONNECTION_DISAMBIGUATED, { source: "wrong_person_add_new", connection_id: connectionUuid.value }, coachingSessionUuid);
    } catch (error) {
        revertActionState({ dismissed: false });
        handleActionFailure(error, "reassign_new");
    } finally {
        router.reload({ only: ["connections"] });
    }
};

const form = reactive({
    name: name.value,
    jobTitle: jobTitle.value,
});

const firstName = computed(() => getFirstName(form.name || name.value) || "this person");

const showAddedState = computed(() => shouldShowSubmitted && isSubmitted.value);

const isFormValid = computed(() => !!form.name?.trim());

const addNewPerson = async () => {
    if (!isFormValid.value) {
        return;
    }

    try {
        await execute(ChatAction.TAGGED_PERSON, {
            message_id: connectionChatMessageAction.message.chat_message_id,
            lineIdx: connectionChatMessageAction.lineIdx,
            index: connectionChatMessageAction.line.index,
            action_state: { submitted: true },
            action_params: {
                name: form.name,
                title: form.jobTitle,
            },
            subaction: "submit",
        });
        logUserInteraction(AmplitudeEventNames.CONNECTION_UPDATE, { source: "chat", connection_id: connectionUuid.value }, coachingSessionUuid);
    } catch (error) {
        revertActionState({ submitted: false });
        handleActionFailure(error, "submit");
    } finally {
        router.reload({ only: ["connections"] });
    }
};

const isProfileLinked = computed(() => !!currentConnection.value?.public_profile);
const isExpanded = ref(false);
const isSearchOpen = ref(false);
const searchError = ref<unknown>(null);

const inviteCopyFailed = ref(false);
const searchQuery = ref("");

const searchedName = computed(() => searchQuery.value.trim() || name.value);
const inviteFirstName = computed(() => getFirstName(searchedName.value) || "them");

const orgLabel = computed(() => {
    const org = organizationDetails.value;
    return !org || org.is_default || !org.name ? "your company" : org.name;
});

// The link affordance belongs to the collapsed card only; expanding swaps in the notes context, whose
// primary action is View Person.
const showLinkSlot = computed(() => isPublicProfileEnabled && !isProfileLinked.value && !isNewConnection.value && !isExpanded.value);

const { show: showCopied, isShowing: hasCopiedInvite } = useTimedShow();
const inviteActions = computed<ComboboxAction[]>(() => [{ id: "copy-invite", label: "Copy invite link", closeOnSelect: false }]);

const inviteLabel = (hasNoMatch: boolean) => {
    if (hasCopiedInvite.value) {
        return "Copied!";
    }
    return hasNoMatch ? "Copy invite link" : "Not here? Copy invite";
};

const handleCopyInvite = async (id: ComboboxAction["id"]) => {
    if (id !== inviteActions.value[0].id) {
        return;
    }

    inviteCopyFailed.value = false;
    if (!(await copyShareUrl(inviteUrl, profileInviteMessage(inviteFirstName.value)))) {
        inviteCopyFailed.value = true;
        return;
    }
    showCopied();
    showChatToast(`Copied! Send the link to ${inviteFirstName.value} so they can complete their Collaboration Profile.`);
    logUserInteraction(AmplitudeEventNames.CONNECTION_INVITE_COPIED, { connection_id: connectionUuid.value, source: "chat" }, coachingSessionUuid);
};

const handleCancelSearch = () => {
    isSearchOpen.value = false;
    logUserInteraction(
        AmplitudeEventNames.CONNECTION_LINK_SEARCH_CANCELED,
        { connection_id: connectionUuid.value, copied_invite: hasCopiedInvite.value, source: "chat" },
        coachingSessionUuid,
    );
};

const {
    linkProfile,
    isLinking,
    error: linkProfileError,
} = useLinkPublicProfile({
    connectionId: connectionUuid,
    source: "chat",
    coachingSessionUuid,
    onLinked: () => {
        router.reload({
            only: ["connections"],
            onSuccess: () => {
                isSearchOpen.value = false;
            },
        });
    },
});

const handleOpenSearch = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_LINK_PROFILE_BUTTON_CLICKED, { connection_id: connectionUuid.value, source: "chat" }, coachingSessionUuid);
    isSearchOpen.value = true;
    inviteCopyFailed.value = false;
};

const onLinkSelect = (profile: ExistingPublicProfile) => {
    linkProfile(profile.user);
};

const handleViewPerson = () => {
    if (connectionUuid.value) {
        logUserInteraction(AmplitudeEventNames.CONNECTION_VIEW_PERSON_CLICKED, { connection_id: connectionUuid.value }, coachingSessionUuid);
        globalThis.open(profileHref.value, "_blank");
    }
};

const logCollaborationProfileClick = () => {
    if (connectionUuid.value) {
        logUserInteraction(AmplitudeEventNames.CONNECTION_COLLABORATION_PROFILE_CLICKED, { connection_id: connectionUuid.value }, coachingSessionUuid);
    }
};

// Built with h() rather than markup so the sentence's spacing comes from these strings
// instead of the template compiler's whitespace condensing.
const ProfilePulledSummary: FunctionalComponent = () => {
    const lead = `Nadia is using ${contextDescription.value}`;
    if (!collaborationProfileShared.value) {
        return `${lead}.`;
    }
    return [
        `${lead}, plus their `,
        h(
            "a",
            {
                href: profileHref.value,
                target: "_blank",
                rel: "noopener",
                "data-testid": "collaboration-profile-link",
                class: "text-valence-primary-500 underline",
                onClick: logCollaborationProfileClick,
            },
            "linked Collaboration Profile",
        ),
        ".",
    ];
};

const onAccordionToggle = (openIds: string[]) => {
    isExpanded.value = openIds.includes("people");
    if (isExpanded.value) {
        // Expanding hides the link slot, so drop any in-progress search rather than silently restoring
        // a stale query when the card is collapsed again.
        isSearchOpen.value = false;
        if (connectionUuid.value) {
            logUserInteraction(AmplitudeEventNames.CONNECTION_CARD_VIEWED, { connection_id: connectionUuid.value }, coachingSessionUuid);
        }
    }
};

const isWrongPersonView = ref(false);
const handleWrongPerson = () => {
    isWrongPersonView.value = true;
};

const handleDecline = async () => {
    try {
        await execute(ChatAction.TAGGED_PERSON, {
            message_id: connectionChatMessageAction.message.chat_message_id,
            lineIdx: connectionChatMessageAction.lineIdx,
            index: connectionChatMessageAction.line.index,
            action_state: { dismissed: true },
            subaction: "dismiss",
        });
        logUserInteraction(AmplitudeEventNames.CONNECTION_DISMISSED, { source: "decline_button", connection_id: connectionUuid.value }, coachingSessionUuid);
    } catch (error) {
        revertActionState({ dismissed: false });
        handleActionFailure(error, "dismiss");
    } finally {
        router.reload({ only: ["connections"] });
    }
};

const isDismissed = computed(() => connectionChatMessageAction.line.action_state?.dismissed);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.status-row {
    @apply scaffold-details-small flex items-center gap-1 whitespace-nowrap px-3 py-1.5 border-b;
}
</style>
